/* eslint-disable max-lines */
/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { utmFormat } from 'utils/useUTM';
import { countryToDataRegion } from 'utils/dataRegion';
import { TranslatableField } from 'types/supportedLanguages';
import { LogWarning } from 'utils/loggingHelpers';
import {
  ActiveEngagement,
  ManifestType,
  OrganizationType,
  PageType,
  ProjectType,
  ThemeType,
} from 'types/publishedAssets';
import { SurveyType } from 'types/survey';
import { QbSurveyInfo } from 'types/qbSurveyInfo';
import { GetFeedResponseBody, FeedType, FeedSort, FeedSortDirection, FeedItem } from 'types/feeds';
import { buildCollabMapMock } from '@/screens/collaborativeMap/services/collaborativeMapMocks';
import type { InternalAxiosRequestConfig } from 'axios';
import { isTokenAllowedForUrl } from '@/components/Auth/tokenScope';
import { createAxiosInstance } from '../utils/axios';
import { SUBMISSION_LINK_TOKEN_HEADER } from './submissionLinkToken';
import type { ChartEmbedPayload } from '../types/chartEmbed';

const axiosInstance = createAxiosInstance('/');

let getEfgToken: () => string | undefined = () => undefined;

export function setEfgTokenProvider(provider: () => string | undefined): void {
  getEfgToken = provider;
}

// One line per path per page load: a withheld token surfaces at the gateway as a
// 401 that looks exactly like an expired session, and the likeliest cause is this
// allowlist drifting from EFG's `@AllowScopedToken` routes (ENG-3878).
const warnedWithheldPaths = new Set<string>();

const warnScopedTokenWithheld = (url?: string): void => {
  const path = (url ?? '').split('?')[0];
  if (warnedWithheldPaths.has(path)) return;
  warnedWithheldPaths.add(path);
  LogWarning('Withheld preferences-scoped token from a gateway request', {
    flow: 'preferences-token-exchange',
    path,
  });
};

/**
 * Attaches the resident's EFG token, unless its scope forbids this route.
 *
 * The scope check lives here rather than in each provider: the provider singleton
 * is last-writer-wins, so a provider that forgot the rule would re-attach a
 * preferences-only token to every request on its page.
 *
 * Exported for the test that asserts both halves of that gating.
 */
export const attachEfgAuthorization = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = getEfgToken();
  if (!token || config.headers.Authorization) return config;

  if (!isTokenAllowedForUrl(token, config.url)) {
    warnScopedTokenWithheld(config.url);
    return config;
  }

  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = `Bearer ${token}`;
  return config;
};

axiosInstance.interceptors.request.use(attachEfgAuthorization);

// note hostname is not available on 404 or error page so have default of /api to make clientside requests
const hostname = process.env.NEXT_PUBLIC_GATEWAY_URL || globalThis?.process?.env?.NEXT_PUBLIC_GATEWAY_URL || '/efg';
const timeout = Number(process.env.AXIOS_TIMEOUT || globalThis?.process?.env?.AXIOS_TIMEOUT || 10000);

const isDisableSubmission =
  process.env.NEXT_PUBLIC_DISABLE_SUBMISSIONS === 'true' ||
  globalThis?.process?.env?.NEXT_PUBLIC_DISABLE_SUBMISSIONS === 'true';

const defaultHeader = {
  headers: {},
};
// for preview link
const createAuthHeader = (accessToken: string) => {
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
  return authHeaders;
};

/**
 * Fetches one public chart embed.
 *
 * No auth header and no query parameters: the token in the path is the whole request, which is
 * what lets the response be cached identically for every viewer.
 */
export const getChartEmbed = async (organization: string, locale: string, token: string) => {
  const response = await axiosInstance.get<ChartEmbedPayload>(
    `${hostname}/${organization}/${locale}/embed/charts/${token}`,
    defaultHeader,
  );

  return response.data;
};

// TODO: handle error codes + retry logic sentry monitoring (currently assume 404)
export const getManifest = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  preview?: boolean,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  let url = `${hostname}/${organization}/${locale}/manifests/engagements/${uuid}`;
  if (preview) url = `${url}/preview`;
  const manifest = await axiosInstance.get<ManifestType>(url, headers);
  return manifest.data;
};

export const getQuestionnaireManifest = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  preview?: boolean,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  let url = `${hostname}/${organization}/${locale}/manifests/questionnaires/${uuid}`;
  if (preview) url = `${url}/preview`;
  const manifest = await axiosInstance.get<ManifestType>(url, headers);
  return manifest.data;
};

/** The share code as a query parameter, encoded so it can only ever be the value of `code`. */
const externalPreviewQuery = (code?: string) => `code=${encodeURIComponent(code ?? '')}`;

export const getExternalPreviewManifest = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  previewCode?: string,
) => {
  const headers = defaultHeader;
  const manifest = await axiosInstance.get<ManifestType>(
    `${hostname}/${organization}/${locale}/manifests/engagements/${uuid}/externalPreview?${externalPreviewQuery(previewCode)}`,
    headers,
  );
  return manifest.data;
};

export const getSurvey = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  version?: string,
  preview?: boolean,
  publishVersion?: number,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  const publishVersionParam = publishVersion ? `?publishVersion=${publishVersion}` : '?publishVersion=2';
  const url = `${hostname}/${organization}/${locale}/engagements/${uuid}${
    preview ? '/preview' : `/versions/${assetVersion}`
  }${publishVersionParam}`;
  const survey = await axiosInstance.get<SurveyType | QbSurveyInfo>(url, headers);
  return survey.data;
};

export const getQuestionnaireSurvey = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  version?: string,
  preview?: boolean,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  const url = `${hostname}/questionnaires/${uuid}${preview ? '/preview' : `/versions/${assetVersion}`}`;
  const survey = await axiosInstance.get<QbSurveyInfo>(url, headers);
  return survey.data;
};

export const getExternalPreview = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  previewCode?: string,
  publishVersion?: number,
) => {
  const axiosConfig = {
    ...defaultHeader,
    timeout: 3000,
  };
  const survey = await axiosInstance.get<SurveyType | QbSurveyInfo>(
    `${hostname}/${organization}/${locale}/engagements/${uuid}/externalPreview?${externalPreviewQuery(previewCode)}&publishVersion=${publishVersion}`,
    axiosConfig,
  );
  return survey.data;
};

export const getTheme = async (
  organization?: string,
  locale?: string,
  version?: string,
  accessToken?: string,
  publishVersion?: number,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  let url = `${hostname}/${organization}/${locale}/brand/versions/${assetVersion}`;
  const publishVersionParam = publishVersion ? `?publishVersion=${publishVersion}` : '?publishVersion=1';
  url = `${url}${publishVersionParam}`;
  const defaultTheme = await axiosInstance.get<ThemeType>(url, headers);
  return defaultTheme.data;
};

export const getThemeWithUuid = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  version?: string,
  accessToken?: string,
  publishVersion?: number,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  let url = `${hostname}/${organization}/${locale}/brand/${uuid}/versions/${assetVersion}`;
  const publishVersionParam = publishVersion ? `?publishVersion=${publishVersion}` : '?publishVersion=1';
  url = `${url}${publishVersionParam}`;
  const defaultTheme = await axiosInstance.get<ThemeType>(url, headers);
  return defaultTheme.data;
};

export const getOrganization = async (
  organization?: string,
  locale?: string,
  version?: string,
  accessToken?: string,
  publishVersion?: number,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  let url = `${hostname}/${organization}/${locale}/organizations/versions/${assetVersion}`;
  const publishVersionParam = publishVersion ? `?publishVersion=${publishVersion}` : '?publishVersion=1';
  url = `${url}${publishVersionParam}`;
  const defaultOrganization = await axiosInstance.get<OrganizationType>(url, { ...headers, timeout });
  return defaultOrganization.data;
};

export const getOrganizationWithUuid = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  version?: string,
  accessToken?: string,
  publishVersion?: number,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  let url = `${hostname}/${organization}/${locale}/organizations/${uuid}/versions/${assetVersion}`;
  const publishVersionParam = publishVersion ? `?publishVersion=${publishVersion}` : '?publishVersion=1';
  url = `${url}${publishVersionParam}`;
  const defaultOrganization = await axiosInstance.get<OrganizationType>(url, { ...headers, timeout });
  return defaultOrganization.data;
};

export type Category = {
  id: string;
  uuid: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  organization: string;
  slug: string;
  restricted: boolean;
  languages: string[];
  prioritize: boolean;
  whitelabelColor: string;
  whitelabelTextColor: string;
  whitelabelBackgroundColor: string;
  customizeBrandElements: boolean;
  logoRedirectUrl: string;
  headingFont: string;
  sansSerifFont: string;
  serifFont: string;
  defaultLanguage: string;
  logo: TranslatableField;
};

export const getCategory = async (
  organization?: string,
  locale?: string,
  category?: string,
  version?: string,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  const categoryResponse = await axiosInstance.get<Category>(
    `${hostname}/${organization}/${locale}/categories/${category}/versions/${assetVersion}`,
    headers,
  );
  return categoryResponse.data;
};

export const getActiveEngagementsByProject = async (organization: string, locale: string, project: string) => {
  const response = await axiosInstance.get<ActiveEngagement[]>(
    `${hostname}/${organization}/${locale}/projects/${project}/active-engagements`,
  );
  return response.data;
};

export const getProject = async (
  organization?: string,
  locale?: string,
  project?: string,
  version?: string,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  const projectResponse = await axiosInstance.get<ProjectType>(
    `${hostname}/${organization}/${locale}/projects/${project}/versions/${assetVersion}`,
    headers,
  );
  if (organization && locale && project) {
    try {
      const activeEngagements = await getActiveEngagementsByProject(organization, locale, project);
      projectResponse.data.activeEngagements = activeEngagements;
    } catch {
      LogWarning('Exception in getProject');
    }
  }
  return projectResponse.data;
};

/**
 * POST: submissions - Fire when user lands on engagement for first time
 */
export const postSubmissions = async (
  engagementUuid: string,
  clientId: string,
  digitalFingerprint: string,
  userAgent: string,
  submissionUuid: string,
  utmCodes: utmFormat,
  engagementVersion: string,
  country?: string | undefined,
  sessionUuid?: string | undefined,
) => {
  if (isDisableSubmission) return null;
  const postSubmissionResponse = await axiosInstance.post(
    `${hostname}/submissions`,
    {
      clientId,
      digitalFingerprint,
      engagementUuid,
      userAgent,
      uuid: submissionUuid,
      utmId: utmCodes.utm_id,
      utmSource: utmCodes.utm_source,
      utmMedium: utmCodes.utm_medium,
      utmCampaign: utmCodes.utm_campaign,
      utmTerm: utmCodes.utm_term,
      utmContent: utmCodes.utm_content,
      referrer: utmCodes.referrer,
      engagementVersion,
      region: country,
      sessionUuid,
    },
    // Lets the gateway link the submission to a logged-in resident from their
    // Descope token (the token is auto-attached when authenticated).
    { headers: { 'Data-Region': countryToDataRegion(country) } },
  );
  return postSubmissionResponse.data as never;
};

/**
 * PUT: responses - Fire when user clicks next to send data to questions
 * {
 *  clientId
 *  engagementUuid
 *  sectionUuid
 *  submissionUuid
 *  history: [
 *    {
 *      method: "POST" | "PATCH" | "DELETE"
 *      data: {
 *        clientId - tbd
 *        comment - string
 *        defaultLanguage - "en-US"
 *        digitalFingerprint - tbd
 *        inputAnswer -
 *        inputOptionUuid - for matrix
 *        language - en-US
 *        questionUuid - from payload
 *        responseUuid - generated by FE
 *        timestamp - iso utc time
 *      }
 *    }
 *  ]
 * }
 */
export const putResponses = async (
  engagementUuid: string,
  sectionUuid: string,
  submissionUuid: string,
  history: never,
  country?: string | undefined,
  sessionUuid?: string | undefined,
) => {
  if (isDisableSubmission) return null;
  const putResponsesRequest = await axiosInstance.put(`${hostname}/responses`, {
    engagementUuid,
    sectionUuid,
    submissionUuid,
    history,
    region: country,
    sessionUuid,
  });
  return putResponsesRequest.data as never;
};

/**
 * POST: section_submissions - Fire when user clicks next mark section as done
 */
export const postSubmissionsSection = async (
  engagementUuid: string,
  clientId: string,
  submissionUuid: string,
  sectionUuid: string,
  country?: string | undefined,
  sessionUuid?: string | undefined,
) => {
  if (isDisableSubmission) return null;
  const postSubmissionResponse = await axiosInstance.post(`${hostname}/section_submissions`, {
    clientId,
    engagementUuid,
    submissionUuid,
    sectionUuid,
    region: country,
    sessionUuid,
  });
  return postSubmissionResponse.data as never;
};

/**
 * POST: submissions/:uuid/resident - Ask for the signed-in resident (read from the session token by
 * the gateway, never sent here) to be linked to a submission that was started anonymously. The link
 * token issued when the submission was created proves this browser started it. An aborted signal
 * cancels the request. The instance's own retry is off: the caller's retry loop is the only retry
 * policy, so one outage cannot multiply into one request per layer.
 */
export const linkSubmissionToResident = async (
  submissionUuid: string,
  linkToken: string,
  country?: string | undefined,
  signal?: AbortSignal,
) => {
  if (isDisableSubmission) return null;
  const linkResponse = await axiosInstance.post(`${hostname}/submissions/${submissionUuid}/resident`, undefined, {
    headers: { 'Data-Region': countryToDataRegion(country), [SUBMISSION_LINK_TOKEN_HEADER]: linkToken },
    signal,
    'axios-retry': { retries: 0 },
  });
  return linkResponse.data as never;
};

/**
 * PATCH: submissions - Fire when engagement is completed. While the submission still holds its link
 * token, the token goes with it, so the gateway may link the signed-in resident if the create call
 * raced the session; without one the completion is saved and links nobody.
 */
export const patchSubmissions = async (
  submissionUuid: string,
  clientId: string,
  engagementUuid: string,
  country?: string | undefined,
  sessionUuid?: string | undefined,
  disqualified?: boolean,
  linkToken?: string,
) => {
  if (isDisableSubmission) return null;
  const patchSubmissionResponse = await axiosInstance.patch(
    `${hostname}/submissions/${submissionUuid}`,
    {
      clientId,
      engagementUuid,
      completedAt: new Date().toISOString(),
      region: country,
      sessionUuid,
      disqualified: !!disqualified,
    },
    {
      headers: {
        'Data-Region': countryToDataRegion(country),
        ...(linkToken ? { [SUBMISSION_LINK_TOKEN_HEADER]: linkToken } : {}),
      },
    },
  );
  return patchSubmissionResponse.data as never;
};
/**
 * GET: Invitations - Validate invitation code.
 * Returns 200 if valid, 404 if code invalid, 403 if code already used
 */
export const validateInvitationCode = async (
  organization: string,
  locale: string,
  engagementUuid: string,
  code: string,
) => {
  const response = await axiosInstance.get(
    `${hostname}/${organization}/${locale}/engagements/${engagementUuid}/invitations/${code}`,
  );
  return response.data as never;
};

export const createSubscriber = async (
  organization: string,
  locale: string,
  engagementUuid: string,
  projectUuid: string,
  subscriberEmail: string,
  notificationPreference: string,
) => {
  const response = await axiosInstance.post(`${hostname}/${organization}/${locale}/subscribers`, {
    email: subscriberEmail,
    notificationPreference,
    engagementUuid,
    projectUuid,
  });
  return response.data as never;
};

export const getErsHealthz = async (organization: string, locale: string, region: string) => {
  const response = await axiosInstance.get(`${hostname}/${organization}/${locale}/engagements/ers/healthz/${region}`);
  return response.data as never;
};

export const checkUserHasAccessToEngagement = async (
  organization: string,
  locale: string,
  engagementUuid: string,
  accessToken: string | undefined,
) => {
  try {
    if (!accessToken) return false;
    const headers = createAuthHeader(accessToken);
    const response = await axiosInstance.get(
      `${hostname}/${organization}/${locale}/engagements/${engagementUuid}/access`,
      { ...headers, timeout },
    );

    if (response.status === 200) return true;
    return false;
  } catch {
    return false;
  }
};

export const getPageManifest = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  preview?: boolean,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  let url = `${hostname}/${organization}/${locale}/manifests/pages/${uuid}`;
  if (preview) url = `${url}/preview`;
  const manifest = await axiosInstance.get<ManifestType>(url, headers);
  return manifest.data;
};

export const getHomePageManifest = async (organization?: string, locale?: string, accessToken?: string) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const url = `${hostname}/${organization}/${locale}/manifests/pages/home`;
  const manifest = await axiosInstance.get<ManifestType>(url, headers);
  return manifest.data;
};

export const getPage = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  version?: string,
  preview?: boolean,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  let url = `${hostname}/${organization}/${locale}/pages/${uuid}`;
  if (preview) {
    url = `${url}/preview`;
  }
  if (version) {
    url = `${url}/versions/${version}`;
  }
  const page = await axiosInstance.get<PageType>(url, headers);
  return page.data;
};

/** Reads the published payload through a share code: the version a scheduled page goes live with. */
interface ExternalPreviewPageOptions {
  published?: boolean;
  version?: string;
}

const externalPreviewPageQuery = (code: string, { published, version }: ExternalPreviewPageOptions = {}) =>
  [
    externalPreviewQuery(code),
    ...(published ? ['published=true'] : []),
    ...(published && version ? [`version=${encodeURIComponent(version)}`] : []),
  ].join('&');

export const getExternalPreviewPageManifest = async (
  organization: string,
  locale: string,
  uuid: string,
  code: string,
  options: Pick<ExternalPreviewPageOptions, 'published'> = {},
) => {
  const url = `${hostname}/${organization}/${locale}/manifests/pages/${uuid}/externalPreview?${externalPreviewPageQuery(code, options)}`;
  const manifest = await axiosInstance.get<ManifestType>(url, defaultHeader);
  return manifest.data;
};

export const getExternalPreviewPage = async (
  organization: string,
  locale: string,
  uuid: string,
  code: string,
  options: ExternalPreviewPageOptions = {},
) => {
  const url = `${hostname}/${organization}/${locale}/pages/${uuid}/externalPreview?${externalPreviewPageQuery(code, options)}`;
  const page = await axiosInstance.get<PageType>(url, defaultHeader);
  return page.data;
};

/** A share code for a page, for a preview that does not build the live page. Empty for an offline page. */
export const getPagePreviewCode = async (organization: string, locale: string, uuid: string, accessToken: string) => {
  const url = `${hostname}/${organization}/${locale}/pages/${uuid}/previewCode`;
  const response = await axiosInstance.get<Pick<PageType, 'external_preview_code' | 'external_preview_expires_at'>>(
    url,
    createAuthHeader(accessToken),
  );
  return response.data;
};

const FEED_PAGINATION_LIMIT = 200;
export const getFeed = async (
  pageId: string,
  feedType: FeedType,
  options?: {
    locale: string;
    organization?: string;
    sort?: FeedSort;
    sortDirection?: FeedSortDirection;
    includeClosed?: boolean;
    includedTagIds?: string[];
    excludedTagIds?: string[];
    statusFilter?: ('open' | 'upcoming' | 'closed')[];
    includedActivityIds?: string[];
    includedPageIds?: string[];
    contentId?: string;
    version?: string;
    preview?: boolean;
  },
  page?: number,
): Promise<GetFeedResponseBody> => {
  const { organization, ...queryParams } = options || ({} as NonNullable<typeof options>);
  const basePath = organization
    ? `${hostname}/${organization}/pages/${pageId}/feeds/${feedType}`
    : `${hostname}/pages/${pageId}/feeds/${feedType}`;
  const url = basePath;
  const result: FeedItem[] = [];
  const currentPage = page || 1;

  try {
    const response = await axiosInstance.get<GetFeedResponseBody>(url, {
      params: { ...queryParams, page: currentPage, limit: FEED_PAGINATION_LIMIT },
    });
    result.push(...response.data);
    const totalPages = parseInt(response?.headers?.['pagination-total-pages'] as string);
    if (totalPages && !Number.isNaN(totalPages) && currentPage < totalPages) {
      const rest = await getFeed(pageId, feedType, options, currentPage + 1);
      result.push(...rest);
    }
    return result;
  } catch {
    return [];
  }
};

export type GetFeedPageResponse = { items: FeedItem[]; totalPages: number };

/**
 * Fetch ONE page of a feed (no recursive fetch-all like getFeed). Used by the manual feed, whose
 * selection has no hard cap: the public site renders page-by-page behind a load-more button, so
 * fetching everything up front would defeat the pagination.
 */
export const getFeedPage = async (
  pageId: string,
  feedType: FeedType,
  options: {
    locale: string;
    organization?: string;
    sort?: FeedSort;
    sortDirection?: FeedSortDirection;
    contentId?: string;
    version?: string;
    preview?: boolean;
  },
  page?: number,
  limit?: number,
): Promise<GetFeedPageResponse> => {
  const { organization, ...queryParams } = options;
  const currentPage = page ?? 1;
  const url = organization
    ? `${hostname}/${organization}/pages/${pageId}/feeds/${feedType}`
    : `${hostname}/pages/${pageId}/feeds/${feedType}`;

  try {
    const response = await axiosInstance.get<GetFeedResponseBody>(url, {
      params: { ...queryParams, page: currentPage, limit: limit ?? FEED_PAGINATION_LIMIT },
    });
    const totalPages = parseInt(response?.headers?.['pagination-total-pages'] as string, 10);

    return { items: response.data || [], totalPages: Number.isNaN(totalPages) ? 1 : totalPages };
  } catch (error) {
    console.error(`Failed to fetch feed page ${currentPage} for ${pageId}/${feedType}:`, error);
    return { items: [], totalPages: 0 };
  }
};

/**
 * Fetch ONE page of a questionnaire-hosted mixed list (survey end screens). Same contract as
 * getFeedPage, but selections hang off a questionnaire rather than a page, so the gateway route
 * is questionnaire-scoped and the organization is mandatory (questionnaires carry no organization
 * of their own server-side).
 */
export const getQuestionnaireFeedPage = async (
  questionnaireId: string,
  options: {
    locale: string;
    organization: string;
    sort?: FeedSort;
    sortDirection?: FeedSortDirection;
    contentId?: string;
    preview?: boolean;
  },
  page?: number,
  limit?: number,
): Promise<GetFeedPageResponse> => {
  const { organization, ...queryParams } = options;
  const currentPage = page ?? 1;
  const url = `${hostname}/${organization}/questionnaires/${questionnaireId}/feeds/mixed_list`;

  try {
    const response = await axiosInstance.get<GetFeedResponseBody>(url, {
      params: { ...queryParams, page: currentPage, limit: limit ?? FEED_PAGINATION_LIMIT },
    });
    const totalPages = parseInt(response?.headers?.['pagination-total-pages'] as string, 10);

    return { items: response.data || [], totalPages: Number.isNaN(totalPages) ? 1 : totalPages };
  } catch (error) {
    console.error(`Failed to fetch mixed list page ${currentPage} for questionnaire ${questionnaireId}:`, error);
    return { items: [], totalPages: 0 };
  }
};

export const getParticipationCounts = async (
  organization: string,
  locale: string,
  customerId: string,
  engagementIds: string[],
  options?: {
    deduplicated?: boolean;
    completed?: boolean;
  },
): Promise<{ [engagementUuid: string]: number }> => {
  const url = `${hostname}/${organization}/${locale}/engagements/participation_counts`;

  try {
    const response = await axiosInstance.get<{ [engagementUuid: string]: number }>(url, {
      params: {
        customerId,
        engagementUuids: engagementIds.join(','),
        deduplicated: options?.deduplicated,
        completed: options?.completed,
      },
    });

    return response.data;
  } catch {
    return {};
  }
};

/**
 * Use the slug to lookup the uuid of the page
 * @param organization
 * @param locale
 * @param slug
 * @returns
 */
export const getUuidWithSlug = async (organization: string, locale: string, slug: string) => {
  const url = `${hostname}/${organization}/${locale}/pages/${slug}/uuid`;

  const response = await axiosInstance.get<{ uuid: string }>(url);

  return response.data.uuid;
};

export const subscribeToNotifications = async (
  subscriberEmail: string,
  page: PageType,
  organization: string,
  locale: string,
  residentCustomPublicationIds?: string[],
  submissionUuid?: string,
) => {
  const { id: pageId, page_type: pageType, customer_id: customerId } = page;
  const response = await axiosInstance.post(`${hostname}/residents/subscribers/sign-up`, {
    email: subscriberEmail,
    pageId,
    pageType,
    customerId,
    organization,
    locale,
    residentCustomPublicationIds,
    // Links the created resident to the survey submission they signed up from.
    submissionUuid,
    // Implicit consent is obtained via the displayed privacy/unsubscribe text in the sign-up form.
    // Product/legal has confirmed this is sufficient — no explicit checkbox is required.
    consent: true,
  });
  return response.data as never;
};

export const getSitemapOrganizations = async () => {
  const response = await axiosInstance.get(`${hostname}/seo/organizations`);
  return response.data as { items: { organizationId: string }[] };
};

export type SitemapPageResponse = {
  items: {
    slug: string;
    title: string;
    id: string;
    pageType: string;
    updatedAt: string;
    releasedAt: string;
    availableLocales: string[];
    organizationId: string;
    defaultLocaleCode: string;
    advertiseOnPages: [];
  }[];
  total: number;
};

export const getSitemapForOrganization = async (organization: string, limit: number, page: number) => {
  const response = await axiosInstance.get(`${hostname}/seo/${organization}?limit=${limit}&page=${page}`);
  return response.data as SitemapPageResponse;
};

/**
 * POST: customers/:customerId/feeds/participation-counts
 * Returns participation counts for engagements (ERS), discussion topics, and idea boards (EAS).
 * At least one of engagementIds, discussionTopicIds, or ideaBoardIds must be provided.
 */
export type CustomerFeedParticipationCountsResponse = {
  engagements?: Record<string, number>;
  discussionTopics?: Record<string, number>;
  ideaBoards?: Record<string, number>;
};

export const getCustomerFeedParticipationCounts = async (
  customerId: string,
  body: {
    engagementIds?: string[];
    discussionTopicIds?: string[];
    ideaBoardIds?: string[];
  },
): Promise<CustomerFeedParticipationCountsResponse> => {
  const url = `${hostname}/customers/${customerId}/feeds/participation-counts`;

  try {
    const response = await axiosInstance.post<CustomerFeedParticipationCountsResponse>(url, body);
    return response.data;
  } catch (error) {
    console.error(`Failed to get customer feed participation counts`, error);
    return {};
  }
};

export const getCollabMapManifest = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  preview?: boolean,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  let url = `${hostname}/${organization}/${locale}/manifests/collaborative-maps/${uuid}`;
  if (preview) url = `${url}/preview`;
  const manifest = await axiosInstance.get<ManifestType>(url, headers);
  return manifest.data;
};

export const getCollabMap = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  version?: string,
  preview?: boolean,
  publishVersion?: number,
  accessToken?: string,
) => {
  const headers = accessToken ? createAuthHeader(accessToken) : defaultHeader;
  const assetVersion = version || 'latest';
  const publishVersionParam = publishVersion ? `?publishVersion=${publishVersion}` : '?publishVersion=2';
  const url = `${hostname}/${organization}/${locale}/collaborative-maps/${uuid}${
    preview ? '/preview' : `/versions/${assetVersion}`
  }${publishVersionParam}`;
  const survey = await axiosInstance.get<QbSurveyInfo>(url, headers);
  return survey.data;
};

export const getCollabMapManifestExternalPreview = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  code?: string,
) => {
  const headers = defaultHeader;
  const url = `${hostname}/${organization}/${locale}/manifests/collaborative-maps/${uuid}/externalPreview?${externalPreviewQuery(code)}`;
  const manifest = await axiosInstance.get<ManifestType>(url, headers);
  return manifest.data;
};

export const getCollabMapExternalPreview = async (
  organization?: string,
  locale?: string,
  uuid?: string,
  code?: string,
  publishVersion?: number,
) => {
  const headers = defaultHeader;
  const url = `${hostname}/${organization}/${locale}/collaborative-maps/${uuid}/externalPreview?${externalPreviewQuery(code)}&publishVersion=${publishVersion}`;
  const survey = await axiosInstance.get<QbSurveyInfo>(url, headers);
  return survey.data;
};

// POST /collaborative-maps/:activityId/submissions
export const postCollabMapSubmission = async (
  activityId: string,
  submissionUuid: string,
  clientId: string,
  region: string,
  fingerprint: string,
  utmCodes: utmFormat,
) => {
  if (isDisableSubmission) return null;
  const response = await axiosInstance.post(
    `${hostname}/collaborative-maps/${activityId}/submissions`,
    {
      clientId,
      uuid: submissionUuid,
      digitalFingerprint: fingerprint,
      userAgent: navigator.userAgent,
      region,
      utmId: utmCodes.utm_id,
      utmSource: utmCodes.utm_source,
      utmMedium: utmCodes.utm_medium,
      utmCampaign: utmCodes.utm_campaign,
      utmTerm: utmCodes.utm_term,
      utmContent: utmCodes.utm_content,
      referrer: utmCodes.referrer,
    },
    { headers: { 'Data-Region': region } },
  );
  return response.data as never;
};

// PATCH /collaborative-maps/:activityId/submissions/:submissionId
export const patchCollabMapSubmission = async (
  activityId: string,
  submissionUuid: string,
  clientId: string,
  region: string,
  patch: { completedAt?: string; abortedAt?: string },
) => {
  if (isDisableSubmission) return null;
  const response = await axiosInstance.patch(
    `${hostname}/collaborative-maps/${activityId}/submissions/${submissionUuid}`,
    { clientId, region, ...patch },
    { headers: { 'Data-Region': region } },
  );
  return response.data as never;
};

// PUT /collaborative-maps/:activityId/submissions/:submissionId/responses
export const putCollabMapResponses = async (
  activityId: string,
  submissionUuid: string,
  sectionUuid: string,
  history: unknown[],
  region: string,
) => {
  if (isDisableSubmission) return null;
  const response = await axiosInstance.put(
    `${hostname}/collaborative-maps/${activityId}/submissions/${submissionUuid}/responses`,
    { sectionUuid, history, region },
    { headers: { 'Data-Region': region } },
  );
  return response.data as never;
};

// POST /collaborative-maps/:activityId/submissions/:submissionId/section-submissions
export const postCollabMapSectionSubmission = async (
  activityId: string,
  submissionUuid: string,
  sectionUuid: string,
  clientId: string,
  region: string,
) => {
  if (isDisableSubmission) return null;
  const response = await axiosInstance.post(
    `${hostname}/collaborative-maps/${activityId}/submissions/${submissionUuid}/section-submissions`,
    { clientId, sectionUuid, region },
    { headers: { 'Data-Region': region } },
  );
  return response.data as never;
};

// GET /residents/:residentId/activities/:activityId/submission-counts
export const getResidentSubmissionCounts = async (
  residentId: string,
  activityId: string,
  region: string,
): Promise<{ completed: string[]; draft: string[]; aborted: string[] }> => {
  const response = await axiosInstance.get(
    `${hostname}/residents/${residentId}/activities/${activityId}/submission-counts`,
    { headers: { 'Data-Region': region } },
  );
  return response.data as { completed: string[]; draft: string[]; aborted: string[] };
};

export interface CollaborativeMapPinResponse {
  lat: number;
  lng: number;
  submissionId: string;
  residentId: string | null;
  questionId: string;
  inputId: string;
  completedAt: string | null;
}

export interface CollaborativeMapClusterResponse {
  lat: number;
  lng: number;
  count: number;
}

export interface CollaborativeMapSubmissionsResponse {
  total: number;
  mode: 'individual' | 'cluster';
  pins?: CollaborativeMapPinResponse[];
  clusters?: CollaborativeMapClusterResponse[];
}

const isCollabMapMock = process.env.NEXT_PUBLIC_COLLAB_MAP_MOCK === 'true';

// GET /:organization/:locale/collaborative-maps/:activityId/submissions?zoom=&bbox=
export interface GetCollabMapSubmissionsParams {
  organization: string;
  locale: string;
  activityId: string;
  region: string;
  zoom: number;
  bbox: string;
  excludeStatuses: string;
  includeResidentIds?: string;
  excludeResidentIds?: string;
  /** Comma-separated submission input ids; the resident filter's Submissions selection. */
  inputIds?: string;
  signal?: AbortSignal;
}

export const getCollabMapSubmissions = async ({
  organization,
  locale,
  activityId,
  region,
  zoom,
  bbox,
  excludeStatuses,
  includeResidentIds,
  excludeResidentIds,
  inputIds,
  signal,
}: GetCollabMapSubmissionsParams): Promise<CollaborativeMapSubmissionsResponse> => {
  if (isCollabMapMock) return buildCollabMapMock();
  const response = await axiosInstance.get<CollaborativeMapSubmissionsResponse>(
    `${hostname}/${organization}/${locale}/collaborative-maps/${activityId}/submissions`,
    {
      headers: { 'Data-Region': region },
      params: { zoom, bbox, excludeStatuses, includeResidentIds, excludeResidentIds, inputIds },
      signal,
    },
  );
  return response.data;
};

export interface SubmissionResponseItemDto {
  responseId: string;
  inputId: string;
  locale: string | null;
  inputAnswer: string;
  translatedInputAnswer: string | null;
  comment: string;
  translatedComment: string | null;
  geometry?: object | null;
  createdAt: string | null;
  // True when the response was flagged by the PII rules; its text fields arrive
  // redacted unless the requesting resident is the submission author.
  piiViolation?: boolean;
}

export interface SubmissionQuestionItemDto {
  questionId: string;
  order: number;
  title: string;
  // Explicit "hidden on purpose" indicator (admin toggle). Optional because older
  // gateway versions signal hiding only through null responses.
  hideResponseFromPublic?: boolean;
  // null means the responses are hidden from the public.
  responses: SubmissionResponseItemDto[] | null;
}

export interface SubmissionSectionItemDto {
  sectionId: string;
  order: number;
  title: string;
  questions: SubmissionQuestionItemDto[];
}

export interface SubmissionDetailDto {
  residentId: string | null;
  likes: number;
  completedAt: string | null;
  data: SubmissionSectionItemDto[];
}

// GET /{organization}/{locale}/submissions/:submissionId/responses
// The viewer's identity is taken from the Descope session bearer token (attached by the
// axios interceptor); when the authenticated viewer is the submission author, PII-flagged
// responses come back with their full text instead of redacted.
export const getCollabMapSubmissionResponses = async (
  submissionId: string,
  region: string,
  organization: string,
  locale: string,
): Promise<SubmissionDetailDto> => {
  const response = await axiosInstance.get(
    `${hostname}/${organization}/${locale}/submissions/${submissionId}/responses`,
    { headers: { 'Data-Region': region } },
  );
  return response.data as SubmissionDetailDto;
};

// POST /:organization/:locale/submissions/:submissionId/likes
export const postCollabMapLike = async (
  submissionId: string,
  region: string,
  organization: string,
  locale: string,
): Promise<void> => {
  await axiosInstance.post(
    `${hostname}/${organization}/${locale}/submissions/${submissionId}/likes`,
    {},
    { headers: { 'Data-Region': region } },
  );
};

// DELETE /:organization/:locale/submissions/:submissionId/likes
export const deleteCollabMapLike = async (
  submissionId: string,
  region: string,
  organization: string,
  locale: string,
): Promise<void> => {
  await axiosInstance.delete(`${hostname}/${organization}/${locale}/submissions/${submissionId}/likes`, {
    headers: { 'Data-Region': region },
  });
};

interface ToxicityMetrics {
  toxicity: number;
  graphic: number;
  harassmentOrAbuse: number;
  hateSpeech: number;
  insult: number;
  profanity: number;
  sexual: number;
  violenceOrThreat: number;
  error: string | null;
}

export interface PiiEntity {
  type: string;
  score: number;
  // Zero-based character offsets into the analyzed text (translatedText when the
  // input was translated to English, otherwise text). Absent on older gateway versions.
  beginOffset?: number;
  endOffset?: number;
}

export interface ModerationResultDto {
  text: string;
  translatedText: string | null;
  toxicityMetrics: ToxicityMetrics | null;
  piiDetection: PiiEntity[];
  piiError: string | null;
}

export interface ModerationResponse {
  results: ModerationResultDto[];
}

// POST /moderation
export const postModeration = async (
  texts: string[],
  language: string,
  region: string,
): Promise<ModerationResponse> => {
  const response = await axiosInstance.post<ModerationResponse>(
    `${hostname}/moderation`,
    { texts, language },
    { headers: { 'Data-Region': region } },
  );
  return response.data;
};

export interface AddressSuggestionDto {
  id: string;
  label: string;
}

export interface AddressLocationDto {
  lat: number;
  lng: number;
}

// POST /address-search/autocomplete — proxies Amazon Location (server-side via the pod IAM role).
export const postAddressAutocomplete = async (
  query: string,
  region: string,
  biasPosition?: [number, number],
): Promise<AddressSuggestionDto[]> => {
  const response = await axiosInstance.post<{ suggestions: AddressSuggestionDto[] }>(
    `${hostname}/address-search/autocomplete`,
    { query, ...(biasPosition ? { biasPosition } : {}) },
    { headers: { 'Data-Region': region } },
  );
  return response.data.suggestions ?? [];
};

// GET /address-search/place/:placeId — resolves a selected suggestion to coordinates.
export const getAddressPlace = async (placeId: string, region: string): Promise<AddressLocationDto> => {
  const response = await axiosInstance.get<AddressLocationDto>(
    `${hostname}/address-search/place/${encodeURIComponent(placeId)}`,
    { headers: { 'Data-Region': region } },
  );
  return response.data;
};

// POST /residents/download-data — resident id is read from the Descope token on the gateway
export const requestResidentDataDownload = async (region: string): Promise<{ residentId: string }> => {
  const response = await axiosInstance.post<{ residentId: string }>(`${hostname}/residents/download-data`, undefined, {
    headers: { 'Data-Region': region },
  });
  return response.data;
};

// POST /residents/delete-account — resident id is read from the Descope token on the gateway
export const requestResidentAccountDeletion = async (region: string): Promise<{ residentId: string }> => {
  const response = await axiosInstance.post<{ residentId: string }>(`${hostname}/residents/delete-account`, undefined, {
    headers: { 'Data-Region': region },
  });
  return response.data;
};

/** Mirrors the gateway DTO; aligns with the platform-sdk ResidentSubscription (with `_id` mapped to `id`). */
export interface ResidentSubscription {
  id: string;
  publicationTypeKey: string;
  residentId: string;
  customerId: string;
  channel: 'email' | 'in-app';
  isEnabled: boolean;
  entityId?: string;
  customPublicationId?: string;
  title?: string;
}

// GET /residents/subscriptions — resident id is read from the Descope token on the gateway
export const getResidentSubscriptions = async (region: string): Promise<ResidentSubscription[]> => {
  const response = await axiosInstance.get<ResidentSubscription[]>(`${hostname}/residents/subscriptions`, {
    headers: { 'Data-Region': region },
  });
  return response.data;
};

// PATCH /residents/subscriptions — bulk toggle every subscription owned by the
// authenticated resident in one call (used by master "Unsubscribe from all").
export const updateAllResidentSubscriptions = async (
  isEnabled: boolean,
  region: string,
): Promise<{ residentId: string; updated: number }> => {
  const response = await axiosInstance.patch<{ residentId: string; updated: number }>(
    `${hostname}/residents/subscriptions`,
    { isEnabled },
    { headers: { 'Data-Region': region } },
  );
  return response.data;
};

// PATCH /residents/subscriptions/:subscriptionId — resident id is read from the Descope token on the gateway
export const updateResidentSubscription = async (
  subscriptionId: string,
  isEnabled: boolean,
  region: string,
): Promise<ResidentSubscription> => {
  const response = await axiosInstance.patch<ResidentSubscription>(
    `${hostname}/residents/subscriptions/${subscriptionId}`,
    { isEnabled },
    { headers: { 'Data-Region': region } },
  );
  return response.data;
};

// PATCH /residents/preferences — resident id is read from the Descope token on the gateway
export const updateResidentLanguagePreference = async (
  language: string,
  region: string,
): Promise<{ residentId: string; language: string }> => {
  const response = await axiosInstance.patch<{ residentId: string; language: string }>(
    `${hostname}/residents/preferences`,
    { language },
    { headers: { 'Data-Region': region } },
  );
  return response.data;
};

// GET /residents/preferences — resident id is read from the Descope token on the gateway
export const getResidentLanguagePreference = async (
  region: string,
): Promise<{ residentId: string; language: string | null }> => {
  const response = await axiosInstance.get<{ residentId: string; language: string | null }>(
    `${hostname}/residents/preferences`,
    { headers: { 'Data-Region': region } },
  );
  return response.data;
};

// GET /submissions/:submissionId/residents/:residentId/likes
export const getResidentLikedSubmission = async (
  submissionId: string,
  residentId: string,
  region: string,
  organization: string,
  locale: string,
): Promise<boolean> => {
  try {
    await axiosInstance.get(
      `${hostname}/${organization}/${locale}/submissions/${submissionId}/residents/${residentId}/likes`,
      { headers: { 'Data-Region': region } },
    );
    return true;
  } catch (err) {
    if (
      err instanceof Error &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return false;
    }
    throw err;
  }
};

export const getFeatureFlags = async (organization: string): Promise<Record<string, boolean>> => {
  const url = `${hostname}/${organization}/feature-flags`;
  const response = await axiosInstance.get<{ featureFlags: Record<string, boolean> }>(url, defaultHeader);
  return response.data.featureFlags ?? {};
};
